import React, { useState, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { fetchOperationsData } from '../../store/slices/operationsSlice';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Modal } from '../../components/ui/modal';
import TransactionReceipt from '../../components/transactions/TransactionReceipt';
import OperationLayout from '../../components/operations/OperationLayout';
import ProviderCard from '../../components/operations/ProviderCard';
import { PROVIDERS } from '../../lib/constants';
import { Method, operationsService } from '../../services/api/operationsService';
import { friendlyError } from '../../services/api/config';
import { addTransaction } from '../../store/slices/transactionsSlice';
import { printService } from '../../services/printService';
import { Transaction } from '../../types';

const STEPS = [
  { label: 'Proveedor' },
  { label: 'Número' },
  { label: 'Opciones' },
  { label: 'Confirmar' },
];

const ACCENT = '#10b981';

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP', minimumFractionDigits: 2 }).format(amount);

const FacturasPageSteps: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [serviceNumber, setServiceNumber] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('');
  const [facturaData, setFacturaData] = useState<any | null>(null);
  const [selectedPaymentOption, setSelectedPaymentOption] = useState<'min' | 'total' | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [currentTransaction, setCurrentTransaction] = useState<Transaction | null>(null);

  const dispatch = useAppDispatch();
  const { wallet } = useAppSelector((state) => state.wallet as any);
  const { invoiceMethods } = useAppSelector((state) => state.operations as any);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!fetchedRef.current && (!invoiceMethods || invoiceMethods.length === 0)) {
      fetchedRef.current = true;
      dispatch(fetchOperationsData());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleProviderSelect = (providerId: string) => {
    setSelectedProvider(providerId);
    setFacturaData(null);
    setError(null);
    setCurrentStep(2);
  };

  const handleServiceNumberInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= 10) setServiceNumber(value);
  };

  const consultarFactura = async () => {
    if (!serviceNumber || !selectedProvider) {
      setError('Debe ingresar el número de servicio y seleccionar un proveedor');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await operationsService.fetchInvoices(serviceNumber, selectedProvider);
      if (response.success && response.data && response.data.length > 0) {
        const inv = response.data[0] as any;
        setFacturaData({
          customerName:     inv.client_name || inv.name || 'Cliente',
          address:          inv.address || '',
          numberInvoices:   inv.number_invoices ?? null,
          dueDate:          inv.dueDate || new Date().toISOString(),
          // totales con cargo
          amount:           parseFloat(String(inv.new_total   ?? inv.total        ?? 0)),
          // base sin cargo
          baseAmount:       parseFloat(String(inv.total       ?? 0)),
          serviceCharge:    parseFloat(String(inv.service_charge     ?? 0)),
          // mínimo
          minAmount:        parseFloat(String(inv.new_min_total ?? inv.amount_min ?? 0)),
          minBase:          parseFloat(String(inv.amount_min   ?? 0)),
          minServiceCharge: parseFloat(String(inv.min_service_charge ?? 0)),
          status: (() => {
            const s = String(inv.state ?? '').toUpperCase();
            if (s === 'L' || s === 'A') return 'Activo';
            if (s === 'S')              return 'Suspendido';
            if (s === 'C')              return 'Cortado';
            if (s === 'V')              return 'Vencido';
            return inv.state || 'Activo';
          })(),
          statusColor: (() => {
            const s = String(inv.state ?? '').toUpperCase();
            return (s === 'L' || s === 'A') ? 'green' : 'red';
          })(),
          provider: selectedProvider,
        });
        setCurrentStep(3);
      } else {
        setError('No se encontró ninguna factura para el número de servicio ingresado');
      }
    } catch (err: any) {
      setError(friendlyError(err.message || 'Error al consultar la factura. Intente nuevamente.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleServiceNumberSubmit = () => {
    if (!serviceNumber) { setError('Ingrese un número de servicio válido'); return; }
    setError(null);
    consultarFactura();
  };

  const handlePaymentOptionSelect = (option: 'min' | 'total') => {
    setSelectedPaymentOption(option);
    setCurrentStep(4);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!serviceNumber || !selectedProvider || !facturaData || !selectedPaymentOption) {
      setError('Complete todos los campos para continuar');
      return;
    }
    const amountToPay = selectedPaymentOption === 'min' ? facturaData.minAmount : facturaData.amount;
    const walletBalance = wallet?.balance ?? 0;
    if (walletBalance < amountToPay) { setError('Saldo insuficiente para realizar este pago'); return; }
    setIsProcessing(true);
    setError(null);
    try {
      const providerName =
        invoiceMethods?.find((p: Method) => p.id === selectedProvider)?.name ||
        PROVIDERS.FACTURAS.find((p: Method) => p.id === selectedProvider)?.name ||
        selectedProvider;
      const transactionId = `fact-${Date.now()}`;
      const response = await operationsService.sendInvoice(
        amountToPay, serviceNumber, facturaData.customerName || 'Cliente', selectedProvider, ''
      );
      if (response.success) {
        const transaction = {
          id:  response.data?.xid || response.data?.transactionId || transactionId,
          xid: response.data?.xid || response.data?.transactionId || undefined,
          url: response.data?.url || undefined,
          type: 'FACTURA' as const,
          amount: amountToPay,
          provider: selectedProvider,
          providerName,
          date: new Date().toISOString(),
          status: 'COMPLETED' as const,
          reference: Math.random().toString(36).substring(2, 10).toUpperCase(),
          details: {
            serviceNumber, provider: selectedProvider, providerName, amount: amountToPay,
            paymentType: selectedPaymentOption === 'min' ? 'Pago Mínimo' : 'Pago Total',
            totalAmount: facturaData.amount, dueDate: facturaData.dueDate,
            customerName: facturaData.customerName, url: response.data?.url,
          },
        };
        dispatch(addTransaction(transaction));
        setSuccess(`Pago de factura realizado con éxito por RD$${amountToPay.toFixed(2)}`);
        setCurrentTransaction(transaction);
        setShowReceiptModal(true);
        setServiceNumber('');
        setSelectedProvider('');
        setFacturaData(null);
        setSelectedPaymentOption(null);
        setCurrentStep(1);
      } else {
        setError('Error al procesar el pago. Respuesta inválida del servidor.');
      }
    } catch (err: any) {
      setError(friendlyError(err.message || 'Error al procesar el pago de la factura. Intente nuevamente.'));
    } finally {
      setIsProcessing(false);
    }
  };

  const providerList: Method[] =
    invoiceMethods && invoiceMethods.length > 0 ? invoiceMethods : PROVIDERS.FACTURAS;

  const renderStepContent = () => {
    switch (currentStep) {

      case 1:
        return (
          <div className="space-y-5 animate-fadeIn">
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Seleccione el proveedor</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {providerList.map((p: Method) => (
                <ProviderCard key={p.id} id={p.id} name={p.name} logo={p.logo}
                  onClick={handleProviderSelect} accentColor={ACCENT} />
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6 animate-fadeIn">
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Ingrese el número de servicio</p>
            <div className="max-w-sm mx-auto">
              <Input
                label="Número de servicio"
                value={serviceNumber} onChange={handleServiceNumberInputChange}
                placeholder="00000000" fullWidth size="touch" type="text"
                readOnly useCustomKeyboard keyboardType="numeric"
              />
            </div>
            <div className="flex justify-between pt-2">
              <Button variant="outline" size="lg" onClick={handleBack}>← Atrás</Button>
              <Button size="lg" onClick={handleServiceNumberSubmit} disabled={!serviceNumber} isLoading={isLoading}>
                {isLoading ? 'Consultando...' : 'Consultar Factura →'}
              </Button>
            </div>
          </div>
        );

      case 3: {
        const providerDisplayName =
          invoiceMethods?.find((p: Method) => p.id === selectedProvider)?.name ||
          PROVIDERS.FACTURAS.find((p: Method) => p.id === selectedProvider)?.name ||
          selectedProvider;
        const isVencida  = facturaData?.statusColor === 'red';
        const hasMin     = (facturaData?.minBase ?? 0) > 0;
        return (
          <div className="space-y-5 animate-fadeIn">
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Detalles y opción de pago</p>

            {/* Factura header */}
            <div className="rounded-2xl border border-slate-100 overflow-hidden">
              {/* Cliente */}
              <div className="px-5 py-4 flex items-center justify-between gap-3"
                style={{ background: 'linear-gradient(135deg,#ecfdf5,#f0fdf4)' }}>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-800 text-base truncate">{facturaData?.customerName}</p>
                  <p className="text-xs text-slate-500">N° {serviceNumber} · {providerDisplayName}</p>
                  {facturaData?.address && (
                    <p className="text-xs text-slate-400 truncate mt-0.5">{facturaData.address}</p>
                  )}
                  {(facturaData?.numberInvoices ?? 0) > 0 && (
                    <p className="text-xs text-amber-600 font-medium mt-0.5">
                      {facturaData.numberInvoices} factura{facturaData.numberInvoices > 1 ? 's' : ''} pendiente{facturaData.numberInvoices > 1 ? 's' : ''}
                    </p>
                  )}
                </div>
                <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                  isVencida ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'
                }`}>{facturaData?.status}</span>
              </div>

              {/* Detalle montos */}
              <div className="divide-y divide-slate-100">
                {/* Mínimo */}
                {hasMin && (
                  <>
                    <div className="px-5 py-2 bg-slate-50">
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Pago Mínimo</p>
                    </div>
                    <div className="flex justify-between items-center px-5 py-2.5">
                      <span className="text-sm text-slate-500">Monto mínimo</span>
                      <span className="text-sm font-semibold text-slate-700">{formatCurrency(facturaData?.minBase ?? 0)}</span>
                    </div>
                    {(facturaData?.minServiceCharge ?? 0) > 0 && (
                      <div className="flex justify-between items-center px-5 py-2.5">
                        <span className="text-sm text-slate-500">Cargo servicio mínimo</span>
                        <span className="text-sm font-semibold text-slate-700">{formatCurrency(facturaData?.minServiceCharge ?? 0)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center px-5 py-2.5 bg-emerald-50">
                      <span className="text-sm font-bold text-emerald-700">Total mínimo</span>
                      <span className="text-base font-black text-emerald-600">{formatCurrency(facturaData?.minAmount ?? 0)}</span>
                    </div>
                  </>
                )}

                {/* Total */}
                <div className="px-5 py-2 bg-slate-50">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Pago Total</p>
                </div>
                <div className="flex justify-between items-center px-5 py-2.5">
                  <span className="text-sm text-slate-500">Monto total</span>
                  <span className="text-sm font-semibold text-slate-700">{formatCurrency(facturaData?.baseAmount ?? 0)}</span>
                </div>
                {(facturaData?.serviceCharge ?? 0) > 0 && (
                  <div className="flex justify-between items-center px-5 py-2.5">
                    <span className="text-sm text-slate-500">Cargo servicio</span>
                    <span className="text-sm font-semibold text-slate-700">{formatCurrency(facturaData?.serviceCharge ?? 0)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center px-5 py-2.5 bg-emerald-50">
                  <span className="text-sm font-bold text-emerald-700">Total a pagar</span>
                  <span className="text-base font-black text-emerald-600">{formatCurrency(facturaData?.amount ?? 0)}</span>
                </div>
              </div>
            </div>

            {/* Opciones de pago */}
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Seleccione una opción:</p>
            <div className={`grid gap-4 ${hasMin ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {hasMin && (
                <button type="button" onClick={() => handlePaymentOptionSelect('min')}
                  className="p-5 rounded-2xl border-2 text-center transition-all duration-150 hover:border-emerald-500 hover:bg-emerald-50 active:scale-95"
                  style={{ borderColor: '#e2e8f0', background: '#fff' }}>
                  <p className="text-sm font-semibold text-slate-600">Pago Mínimo</p>
                  {(facturaData?.minServiceCharge ?? 0) > 0 && (
                    <p className="text-xs text-slate-400 mt-1">+ {formatCurrency(facturaData?.minServiceCharge ?? 0)} cargo</p>
                  )}
                  <p className="text-xl font-black mt-2" style={{ color: ACCENT }}>
                    {formatCurrency(facturaData?.minAmount ?? 0)}
                  </p>
                </button>
              )}
              <button type="button" onClick={() => handlePaymentOptionSelect('total')}
                className="p-5 rounded-2xl border-2 text-center transition-all duration-150 hover:border-emerald-500 hover:bg-emerald-50 active:scale-95"
                style={{ borderColor: '#e2e8f0', background: '#fff' }}>
                <p className="text-sm font-semibold text-slate-600">Pago Total</p>
                {(facturaData?.serviceCharge ?? 0) > 0 && (
                  <p className="text-xs text-slate-400 mt-1">+ {formatCurrency(facturaData?.serviceCharge ?? 0)} cargo</p>
                )}
                <p className="text-xl font-black mt-2" style={{ color: ACCENT }}>
                  {formatCurrency(facturaData?.amount ?? 0)}
                </p>
              </button>
            </div>

            <Button variant="outline" size="lg" onClick={handleBack}>← Atrás</Button>
          </div>
        );
      }

      case 4: {
        const amountToPay = selectedPaymentOption === 'min' ? (facturaData?.minAmount ?? 0) : (facturaData?.amount ?? 0);
        const paymentTypeText = selectedPaymentOption === 'min' ? 'Pago Mínimo' : 'Pago Total';
        const providerDisplayName4 =
          invoiceMethods?.find((p: Method) => p.id === selectedProvider)?.name ||
          PROVIDERS.FACTURAS.find((p: Method) => p.id === selectedProvider)?.name ||
          selectedProvider;
        return (
          <div className="space-y-6 animate-fadeIn">
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Confirmar pago</p>
            <div className="rounded-2xl border border-slate-100 divide-y divide-slate-100 overflow-hidden"
              style={{ background: '#f8fafc' }}>
              {[
                { label: 'Proveedor',    value: providerDisplayName4 },
                { label: 'Cliente',      value: facturaData?.customerName },
                { label: 'N° Servicio',  value: serviceNumber },
                { label: 'Tipo de pago', value: paymentTypeText },
              ].map(({ label, value }) => (
                <div key={label} className="flex justify-between items-center px-5 py-4">
                  <span className="text-sm text-slate-500 font-medium">{label}</span>
                  <span className="text-sm font-bold text-slate-800">{value}</span>
                </div>
              ))}
              <div className="flex justify-between items-center px-5 py-4" style={{ background: `${ACCENT}0d` }}>
                <span className="text-sm text-slate-500 font-medium">Monto a pagar</span>
                <span className="text-2xl font-black" style={{ color: ACCENT }}>{formatCurrency(amountToPay)}</span>
              </div>
            </div>
            <Button size="touch" fullWidth isLoading={isProcessing} onClick={handleSubmit}>
              {isProcessing ? 'Procesando...' : '✓ Confirmar Pago'}
            </Button>
            <Button variant="outline" size="lg" onClick={handleBack} disabled={isProcessing}>← Atrás</Button>
          </div>
        );
      }

      default: return null;
    }
  };

  return (
    <>
      <OperationLayout
        title="Pago de Facturas"
        description="Pague facturas de servicios básicos y otros servicios"
        steps={STEPS} currentStep={currentStep}
        error={error} success={success} accentColor={ACCENT}
        icon={
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
        }
      >
        {renderStepContent()}
      </OperationLayout>

      <Modal isOpen={showReceiptModal} onClose={() => setShowReceiptModal(false)}
        title="Resumen de Transacción" size="md">
        {currentTransaction && (
          <TransactionReceipt
            transaction={currentTransaction}
            onClose={() => setShowReceiptModal(false)}
            onPrint={() => { if (currentTransaction) printService.printTicket(currentTransaction); }}
          />
        )}
      </Modal>
    </>
  );
};

export default FacturasPageSteps;
